import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConsumerLayout from '@/components/ConsumerLayout';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <ConsumerLayout darkTheme className="flex flex-col">
      
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Animated 404 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="relative inline-block">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full blur-3xl opacity-20" />
              
              {/* 404 text with star */}
              <div className="relative flex items-center justify-center gap-4">
                <span className="text-8xl sm:text-9xl font-bold bg-gradient-to-br from-slate-400 to-slate-600 bg-clip-text text-transparent">
                  4
                </span>
                <div className="relative">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <svg viewBox="0 0 24 24" className="w-20 h-20 sm:w-24 sm:h-24" fill="none">
                      <defs>
                        <linearGradient id="notFoundStarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#fbbf24" />
                          <stop offset="50%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#6366f1" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        fill="url(#notFoundStarGradient)"
                      />
                    </svg>
                  </motion.div>
                </div>
                <span className="text-8xl sm:text-9xl font-bold bg-gradient-to-br from-slate-400 to-slate-600 bg-clip-text text-transparent">
                  4
                </span>
              </div>
            </div>
          </motion.div>
          
          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="headline-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Oops! This page got lost in the night
            </h1>
            <p className="text-lg text-slate-400 mb-8 max-w-md mx-auto">
              The page you're looking for doesn't exist or has been moved. Let's get you back to designing your perfect night out.
            </p>
          </motion.div>
          
          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white"
              onClick={() => navigate('/')}
            >
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </Button>
          </motion.div>
          
          {/* Quick links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12 pt-8 border-t border-slate-800"
          >
            <p className="text-sm text-slate-500 mb-4">Or try one of these:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-indigo-400"
                onClick={() => navigate('/creators')}
                data-testid="notfound-communities-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Browse Communities
              </Button>
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-purple-400"
                onClick={() => navigate('/my-hubs')}
                data-testid="notfound-hub-btn"
              >
                <Compass className="w-4 h-4 mr-2" />
                Create Your Hub
              </Button>
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-amber-400"
                onClick={() => navigate('/pricing')}
                data-testid="notfound-pricing-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </main>
      
    </ConsumerLayout>
  );
};

export default NotFound;
